/**
 * How the jobs results tab loads selected result rows into the workspace: as few load requests
 * as it can, one per job (rather than one per row), each carrying every row's molecule.
 *
 * One request per job is what lets a job's compounds arrive together and be grouped straight
 * away, instead of appearing one at a time and then jumping into their group; it also saves a
 * bfd-server round trip per row. The server loads every molecule in the request (see
 * `loading-manager.elp`'s load path in bfd-server), so the work here is building that request
 * and telling, afterwards, which loaded compound came from which row -- the server can drop
 * molecules it won't import as compounds (ions, waters, polymers), so position alone isn't
 * proof for SDF, which can carry a tag saying.
 *
 * Works on the tab's pending loads: one per row, with the format, molecule data and (for
 * REINVENT) source compound the tab worked out for it. Pure functions, so they can be tested
 * without the tab's UI.
 */
import { withSdfField } from 'BMapsUtil/mol_format_utils';

/** SDF data field carrying a row's position in its load request; removed once matched. */
export const ROW_TAG = 'tmpRow';

/** @typedef {import('BMapsModel/compound').Compound} Compound */

/**
 * What these functions need of a pending load: one result row, ready to load. Callers' entries
 * can carry more (whatever they need once it has loaded); the functions below are generic over
 * the entry type, so those fields come back typed in each request and matched pair.
 * @typedef {object} PendingLoad
 * @property {string} jobid
 * @property {'sdf'|'mol'|'smi'} format
 * @property {string} moldata
 * @property {string} [sourceSpec] The loaded compound it was generated from (REINVENT).
 */

/**
 * @template {PendingLoad} [T=PendingLoad]
 * @typedef {object} LoadRequest
 * @property {string} jobid
 * @property {'sdf'|'mol'|'smi'} format
 * @property {string} [sourceSpec] The source compound shared by its items.
 * @property {T[]} items
 */

/**
 * Split pending loads into load requests: one per job, except that loads of one job with
 * different source compounds go in separate requests (the server names a compound loaded
 * without a name after its source, and a request carries only one), and `mol` loads go one at a
 * time (no job returns them today, and they can carry no tag).
 * @template {PendingLoad} T
 * @param {T[]} items
 * @returns {LoadRequest<T>[]} In the order the items came in.
 */
export function planLoadRequests(items) {
    const requests = [];
    const byKey = new Map();
    for (const item of items) {
        const { jobid, format, sourceSpec } = item;
        const key = format === 'mol'
            ? Symbol('mol load')
            : JSON.stringify([`${jobid}`, format, sourceSpec ?? null]);
        let request = byKey.get(key);
        if (!request) {
            request = {
                jobid, format, sourceSpec, items: [],
            };
            byKey.set(key, request);
            requests.push(request);
        }
        request.items.push(item);
    }
    return requests;
}

/**
 * @param {LoadRequest} request
 * @returns {string} The molecule data for one load request: the items' SDF records, each tagged
 *   with its position, or their SMILES one per line.
 */
export function requestMolData({ format, items }) {
    switch (format) {
        case 'sdf': return items.map((item, i) => withSdfField(item.moldata, ROW_TAG, i)).join('');
        case 'smi': return items.map((item) => item.moldata).join('\n');
        default: return items[0].moldata;
    }
}

/**
 * Pair a request's loaded compounds with the items they came from. SDF compounds by their
 * position tag. SMILES compounds by order, only when every item produced exactly one compound:
 * with any missing, there's no telling which, and a guess would give a compound another
 * molecule's properties and heritage.
 * @template {PendingLoad} T
 * @param {LoadRequest<T>} request
 * @param {Compound[]} compounds As the load returned them.
 * @param {(compound: Compound) => string|undefined} rowTagOf The compound's ROW_TAG value.
 * @returns {{ pairs: Array<[Compound, T]>, unmatched: T[] }}
 */
export function matchCompoundsToItems({ format, items }, compounds, rowTagOf) {
    let pairs = [];
    if (format === 'sdf') {
        for (const compound of compounds) {
            const tag = rowTagOf(compound);
            const item = tag == null || tag === '' ? undefined : items[Number(tag)];
            if (item) pairs.push([compound, item]);
        }
    } else if (compounds.length === items.length) {
        pairs = compounds.map((compound, i) => [compound, items[i]]);
    } else if (items.length === 1 && compounds.length > 0) {
        // One row loaded on its own: its compound is the first, as loading one row always did.
        pairs = [[compounds[0], items[0]]];
    }
    const matched = new Set(pairs.map(([, item]) => item));
    return { pairs, unmatched: items.filter((item) => !matched.has(item)) };
}
